import { authGeneration, authSignal, reportSessionExpired } from '../auth/session'
import { CONNECTION_STATES, type ClientAccount, type Connection, type ConnectionState } from './account'
import type { Client } from './clients'
import type { Role } from '../lib/roles'

export interface Company {
  id: string
  name: string
  /** The user's onboarding demo company (GRO-131). Absent from an older backend: `false`. */
  isDemo?: boolean
  accounts: ClientAccount[]
}

export type FetchCompaniesResult =
  | { status: 'ok'; companies: Company[] }
  | { status: 'unavailable' }

export type CreateCompanyResult =
  | { status: 'ok'; company: Company }
  | { status: 'duplicate' }
  | { status: 'reserved' }
  | { status: 'unavailable' }

/**
 * What one send of a key-addressed create or cancel proved (GRO-82 design, D2). `noRoute`
 * is a 404: this request reached a backend without the route, which says nothing about an
 * earlier send of the same key. `unknown` is everything that settles nothing: no answer,
 * an abort, an unreadable body, a 5xx, a 401 or 403, or a client defect (400, 409
 * `key_reused`). `duplicate` is a `409 duplicate_name`: the name is already in the user's
 * switcher, and the refusal is recorded against the key, so the key is spent (GRO-130).
 */
export type CreationResult =
  | { status: 'company'; company: Company }
  | { status: 'duplicate' }
  | { status: 'cancelled' }
  | { status: 'gone' }
  | { status: 'noRoute' }
  | { status: 'unknown' }

export interface CreationOptions {
  /** The caller's own abort, combined with `authSignal()` for the request and its body. */
  signal?: AbortSignal
  fetchImpl?: typeof fetch
}

/**
 * Calls only `GET /api/companies`, strictly after the session resolves authenticated.
 *
 * The generation this request was issued under is captured before it goes out and checked
 * twice, the same way creatives/api.ts's `send()` guards a session-scoped request: once
 * when the response arrives, and again after its body has been read, so a response or a
 * body read that only finishes after sign-out or expiry is discarded rather than reported
 * as this session's own 401. The request is also bound to `authSignal()`, so it is
 * actually aborted rather than left to resolve into a result nobody will read.
 */
export async function fetchCompanies(
  fetchImpl: typeof fetch = fetch,
  signal?: AbortSignal,
): Promise<FetchCompaniesResult> {
  const generation = authGeneration()
  let response: Response
  try {
    response = await fetchImpl('/api/companies', { credentials: 'same-origin', signal: withAuth(signal) })
  } catch {
    return { status: 'unavailable' }
  }
  if (generation !== authGeneration()) {
    return { status: 'unavailable' }
  }
  if (response.status === 401) {
    // The same treatment creatives/api.ts's send() gives every other authenticated route:
    // an expired session surfaces as a sign-out, not a stuck retry loop.
    reportSessionExpired()
    return { status: 'unavailable' }
  }
  if (!response.ok) {
    return { status: 'unavailable' }
  }
  try {
    const companies = companiesFrom(await response.json())
    if (generation !== authGeneration()) {
      return { status: 'unavailable' }
    }
    return companies === null ? { status: 'unavailable' } : { status: 'ok', companies }
  } catch {
    return { status: 'unavailable' }
  }
}

/**
 * `POST /api/companies`; `name` may be empty only on a first run, where it creates the demo
 * company alone (GRO-131). `reserved` is a first-run name equal to the demo company's.
 * Generation-guarded and `authSignal()`-bound the same way `fetchCompanies` is: a caller
 * (`FirstRun`'s `finish()`, `AuthBoundary`'s adoption path) still checks the generation it
 * captured before calling this against the one current when it resolves, since a company
 * can only ever be created once per call and creating it is not itself undone by discarding
 * the result.
 */
export async function createCompany(name: string, fetchImpl: typeof fetch = fetch): Promise<CreateCompanyResult> {
  const generation = authGeneration()
  let response: Response
  try {
    response = await fetchImpl('/api/companies', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
      signal: authSignal(),
    })
  } catch {
    return { status: 'unavailable' }
  }
  if (generation !== authGeneration()) {
    return { status: 'unavailable' }
  }
  if (response.status === 401) {
    reportSessionExpired()
    return { status: 'unavailable' }
  }
  if (response.status === 409) {
    const code = await response.json().then(errorCodeOf, () => null)
    if (generation !== authGeneration()) return { status: 'unavailable' }
    if (code === 'duplicate_name') return { status: 'duplicate' }
    return code === 'reserved_name' ? { status: 'reserved' } : { status: 'unavailable' }
  }
  if (!response.ok) {
    return { status: 'unavailable' }
  }
  try {
    const company = companyFrom(await response.json())
    if (generation !== authGeneration()) {
      return { status: 'unavailable' }
    }
    return company === null ? { status: 'unavailable' } : { status: 'ok', company }
  } catch {
    return { status: 'unavailable' }
  }
}

/**
 * `PUT /api/company-creations/{key}`: the create that a retry cannot repeat (GRO-82). The
 * form only ever calls this, never `createCompany`, so a backend without the route fails
 * closed with a 404 instead of creating a second company.
 */
export function putCompanyCreation(key: string, name: string, options: CreationOptions = {}): Promise<CreationResult> {
  return sendCreation('PUT', key, JSON.stringify({ name }), options)
}

/** `DELETE /api/company-creations/{key}`: cancels an unused key, or reads back the company a spent one created. */
export function deleteCompanyCreation(key: string, options: CreationOptions = {}): Promise<CreationResult> {
  return sendCreation('DELETE', key, undefined, options)
}

const UNKNOWN: CreationResult = { status: 'unknown' }

function withAuth(signal: AbortSignal | undefined): AbortSignal {
  return signal === undefined ? authSignal() : AbortSignal.any([signal, authSignal()])
}

/**
 * The body, or a rejection as soon as `signal` aborts. A real fetch already fails its body
 * read on abort; racing it here makes that hold for any `fetchImpl`, so an abort while the
 * body is still arriving always ends the send.
 */
function readJson(response: Response, signal: AbortSignal): Promise<unknown> {
  if (signal.aborted) return Promise.reject(signal.reason)
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(signal.reason)
    signal.addEventListener('abort', onAbort, { once: true })
    response.json().then(resolve, reject).finally(() => signal.removeEventListener('abort', onAbort))
  })
}

/**
 * Generation-guarded and bound to the session's signal the way `createCompany` is: an
 * answer that arrives after sign-out or expiry is `unknown`, never this session's outcome.
 */
async function sendCreation(
  method: 'PUT' | 'DELETE',
  key: string,
  body: string | undefined,
  { signal, fetchImpl = fetch }: CreationOptions,
): Promise<CreationResult> {
  const generation = authGeneration()
  const combined = withAuth(signal)
  let response: Response
  try {
    response = await fetchImpl(`/api/company-creations/${encodeURIComponent(key)}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body,
      signal: combined,
    })
  } catch {
    return UNKNOWN
  }
  if (generation !== authGeneration()) return UNKNOWN
  switch (response.status) {
    case 401:
      reportSessionExpired()
      return UNKNOWN
    case 404:
      return { status: 'noRoute' }
    case 204:
      return { status: 'cancelled' }
    case 410:
      return { status: 'gone' }
  }
  let parsed: unknown
  try {
    parsed = await readJson(response, combined)
  } catch {
    parsed = undefined
  }
  if (generation !== authGeneration()) return UNKNOWN
  if (response.status === 200 || response.status === 201) {
    const company = parsed === undefined ? null : companyFrom(parsed)
    return company === null ? UNKNOWN : { status: 'company', company }
  }
  if (response.status === 409 || response.status === 400) {
    const code = errorCodeOf(parsed)
    if (response.status === 409 && code === 'creation_cancelled') return { status: 'cancelled' }
    if (response.status === 409 && code === 'duplicate_name') return { status: 'duplicate' }
    // A client defect rather than an outcome: the key was reused under another name, or
    // the request was malformed. The attempt stays unresolved; the console keeps the code.
    console.error(`company-creations ${method} answered ${response.status} ${code ?? '(no code)'}`)
  }
  return UNKNOWN
}

function errorCodeOf(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null
  const code = (body as { error?: unknown }).error
  return typeof code === 'string' ? code : null
}

function companiesFrom(body: unknown): Company[] | null {
  if (typeof body !== 'object' || body === null) return null
  const raw = (body as { companies?: unknown }).companies
  if (!Array.isArray(raw)) return null
  const companies: Company[] = []
  for (const entry of raw) {
    const company = companyOf(entry)
    if (company === null) return null
    companies.push(company)
  }
  return companies
}

function companyFrom(body: unknown): Company | null {
  if (typeof body !== 'object' || body === null) return null
  return companyOf((body as { company?: unknown }).company)
}

function companyOf(value: unknown): Company | null {
  if (typeof value !== 'object' || value === null) return null
  const id = (value as { id?: unknown }).id
  const name = (value as { name?: unknown }).name
  const rawAccounts = (value as { accounts?: unknown }).accounts
  const isDemo = (value as { isDemo?: unknown }).isDemo === true
  if (typeof id !== 'string' || id === '') return null
  if (typeof name !== 'string') return null
  if (!Array.isArray(rawAccounts)) return null
  const accounts: ClientAccount[] = []
  for (const entry of rawAccounts) {
    const account = accountOf(entry)
    if (account === null) return null
    accounts.push(account)
  }
  return { id, name, isDemo, accounts }
}

function accountOf(value: unknown): ClientAccount | null {
  if (typeof value !== 'object' || value === null) return null
  const id = (value as { id?: unknown }).id
  const name = (value as { name?: unknown }).name
  const isDemo = (value as { isDemo?: unknown }).isDemo
  if (typeof id !== 'string' || id === '') return null
  if (typeof name !== 'string') return null
  if (typeof isDemo !== 'boolean') return null
  const account: ClientAccount = { id, name, isDemo, ...labelsOf(value) }
  const raw = (value as { connection?: unknown }).connection
  if (raw === undefined) return account
  const connection = connectionOf(raw)
  if (connection === null) return null
  return connection === 'unknown' ? account : { ...account, connection }
}

/**
 * The currency and portfolio, kept only when each is a string. Lenient where the
 * connection is strict: a label that cannot be read hides itself, not the company.
 */
function labelsOf(value: object): Pick<ClientAccount, 'currency' | 'portfolioName'> {
  const { currency, portfolioName } = value as { currency?: unknown; portfolioName?: unknown }
  return {
    ...(typeof currency === 'string' && { currency }),
    ...(typeof portfolioName === 'string' && { portfolioName }),
  }
}

/**
 * Strict on shape, lenient on vocabulary: a malformed connection rejects the company the
 * way any other malformed field does, while a well-formed state this build does not know
 * yet reads as `'unknown'` and renders no state line, so a later server can add one.
 */
function connectionOf(value: unknown): Connection | 'unknown' | null {
  if (typeof value !== 'object' || value === null) return null
  const state = (value as { state?: unknown }).state
  const pulled = (value as { lastSuccessfulPullAt?: unknown }).lastSuccessfulPullAt
  const expires = (value as { credentialExpiresAt?: unknown }).credentialExpiresAt
  if (typeof state !== 'string') return null
  if (pulled !== null && typeof pulled !== 'string') return null
  if (expires !== null && typeof expires !== 'string') return null
  if (!(CONNECTION_STATES as readonly string[]).includes(state)) return 'unknown'
  return { state: state as ConnectionState, lastSuccessfulPullAt: pulled, credentialExpiresAt: expires }
}

/**
 * The client is demo when the company is the demo company (GRO-131, D3). A real company with
 * no accounts yet is not demo, and neither is a legacy real company that still holds its
 * demo account beside the real ones.
 */
export function toClient(company: Company, role: Role): Client {
  return {
    id: company.id,
    name: company.name,
    accounts: company.accounts,
    isDemo: company.isDemo === true,
    role,
  }
}
